import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLParagraphs = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Content & Typography</div>
                <h1>Paragraphs & Text Formatting</h1>
                <p className="hero-lead">
                    Master the art of text content with paragraphs, line breaks, and semantic text formatting
                    elements for professional, accessible web pages.
                </p>
            </div>

            <div className="content-section">
                <h2>The Paragraph Element</h2>
                <p>
                    The <code>&lt;p&gt;</code> element defines a paragraph of text. It's one of the most commonly
                    used HTML elements for body content.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Paragraph Usage</span>
                    </div>
                    <CodeBlock language="html" code={`<p>This is a paragraph of text. Browsers automatically add spacing before and after paragraphs.</p>

<p>This is another paragraph. Each paragraph is a separate block element.</p>

<p>Paragraphs can contain <strong>bold text</strong>, <em>italic text</em>, and <a href="#">links</a>.</p>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Key Behavior:</strong> Paragraphs are block-level elements. They start on a new line
                        and browsers add default margin above and below them.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Line Breaks: &lt;br&gt; vs. &lt;p&gt;</h2>
                <p>
                    Understanding when to use <code>&lt;br&gt;</code> (line break) versus <code>&lt;p&gt;</code> (new paragraph)
                    is crucial for semantic HTML.
                </p>

                <div className="comparison-table">
                    <h3>When to Use Each:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Element</th>
                                <th>Purpose</th>
                                <th>Use Cases</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;p&gt;</code></td>
                                <td>Separate paragraphs</td>
                                <td>Body text, distinct ideas, spacing between blocks</td>
                            </tr>
                            <tr>
                                <td><code>&lt;br&gt;</code></td>
                                <td>Line break within content</td>
                                <td>Poems, addresses, lyrics, single lines within a paragraph</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct Usage: Poem (use &lt;br&gt;)</span>
                    </div>
                    <CodeBlock language="html" code={`<p>
    Roses are red,<br>
    Violets are blue,<br>
    HTML is awesome,<br>
    And so are you!
</p>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct Usage: Address (use &lt;br&gt;)</span>
                    </div>
                    <CodeBlock language="html" code={`<p>
    John Doe<br>
    123 Main Street<br>
    New York, NY 10001<br>
    United States
</p>`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Wrong: Multiple &lt;br&gt; for spacing</span>
                    </div>
                    <CodeBlock language="html" code={`<p>First paragraph</p>
<br><br><br>  <!-- Wrong! Use CSS margin instead --&gt;
<p>Second paragraph</p>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct: Use CSS for spacing</span>
                    </div>
                    <CodeBlock language="html" code={`<p>First paragraph</p>
<p style="margin-top: 2rem;">Second paragraph with extra spacing</p>

<!-- Or better yet, use a CSS class --&gt;
<p>First paragraph</p>
<p class="spaced-paragraph">Second paragraph</p>

<style>
.spaced-paragraph {
    margin-top: 2rem;
}
</style>`} />
                </div>

                <Warning><strong> Rule of Thumb:</strong> If you're using more than two <code>&lt;br&gt;</code> tags in a row,
                    you should probably be using paragraphs or CSS spacing instead.</Warning>
            </div>

            <div className="content-section">
                <h2>Semantic Text Formatting Elements</h2>
                <p>
                    HTML provides semantic elements that convey <em>meaning</em> along with formatting.
                    Always prefer these over purely visual elements.
                </p>

                <h3>Emphasis Elements</h3>
                <div className="element-showcase">
                    <div className="element-card">
                        <h4><code>&lt;strong&gt;</code> - Strong Importance</h4>
                        <p><strong>Renders as:</strong> <strong>Bold text</strong></p>
                        <p><strong>Meaning:</strong> Indicates strong importance, seriousness, or urgency</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p><strong>Warning:</strong> This action cannot be undone.</p>
<p>The <strong>most important</strong> thing to remember is...</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h4><code>&lt;em&gt;</code> - Emphasis</h4>
                        <p><strong>Renders as:</strong> <em>Italic text</em></p>
                        <p><strong>Meaning:</strong> Stresses emphasis, changes meaning of sentence</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>I <em>love</em> HTML!</p>
<p>We <em>must</em> finish this today.</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h4><code>&lt;b&gt;</code> - Bring Attention To</h4>
                        <p><strong>Renders as:</strong> <b>Bold text</b> (but without semantic importance)</p>
                        <p><strong>Meaning:</strong> Draw attention without conveying extra importance</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The <b>product name</b> is displayed in bold.</p>
<p><b>Keywords:</b> HTML, CSS, JavaScript</p>`} />
                        </div>
                    </div>

                    <div className="element-card">
                        <h4><code>&lt;i&gt;</code> - Alternate Voice</h4>
                        <p><strong>Renders as:</strong> <i>Italic text</i> (but without emphasis)</p>
                        <p><strong>Meaning:</strong> Technical terms, foreign phrases, thoughts</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The French phrase <i>je ne sais quoi</i> is hard to translate.</p>
<p><i>Homo sapiens</i> is the scientific name for humans.</p>`} />
                        </div>
                    </div>
                </div>

                <div className="decision-table">
                    <h3>Which Element Should I Use?</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Scenario</th>
                                <th>Use This</th>
                                <th>Not This</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Important information</td>
                                <td><code>&lt;strong&gt;</code></td>
                                <td><code>&lt;b&gt;</code></td>
                            </tr>
                            <tr>
                                <td>Stress emphasis</td>
                                <td><code>&lt;em&gt;</code></td>
                                <td><code>&lt;i&gt;</code></td>
                            </tr>
                            <tr>
                                <td>Product names, keywords</td>
                                <td><code>&lt;b&gt;</code></td>
                                <td><code>&lt;strong&gt;</code></td>
                            </tr>
                            <tr>
                                <td>Foreign words, scientific names</td>
                                <td><code>&lt;i&gt;</code></td>
                                <td><code>&lt;em&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Other Text Formatting Elements</h2>

                <div className="formatting-grid">
                    <div className="format-card">
                        <h4><code>&lt;mark&gt;</code> - Highlighted Text</h4>
                        <p><strong>Renders as:</strong> <mark>Highlighted text</mark></p>
                        <p><strong>Use for:</strong> Search results, relevant references</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Search results for "<mark>HTML tutorial</mark>"</p>`} />
                        </div>
                    </div>

                    <div className="format-card">
                        <h4><code>&lt;small&gt;</code> - Fine Print</h4>
                        <p><strong>Renders as:</strong> <small>Smaller text</small></p>
                        <p><strong>Use for:</strong> Side comments, disclaimers, legal text</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Price: $99 <small>(tax not included)</small></p>
<p><small>Terms and conditions apply.</small></p>`} />
                        </div>
                    </div>

                    <div className="format-card">
                        <h4><code>&lt;del&gt;</code> and <code>&lt;ins&gt;</code> - Edits</h4>
                        <p><strong>Renders as:</strong> <del>Deleted text</del> and <ins>Inserted text</ins></p>
                        <p><strong>Use for:</strong> Document revisions, showing changes</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Price: <del>$199</del> <ins>$149</ins> (25% off!)</p>
<p>Meeting time: <del{"&gt;"} 3 PM</del> <ins>4 PM</ins></p>`} />
                        </div>
                    </div>

                    <div className="format-card">
                        <h4><code>&lt;s&gt;</code> - Strikethrough</h4>
                        <p><strong>Renders as:</strong> <s>Strikethrough text</s></p>
                        <p><strong>Use for:</strong> No longer accurate or relevant</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p><s>Out of stock</s> Now available!</p>`} />
                        </div>
                    </div>

                    <div className="format-card">
                        <h4><code>&lt;u&gt;</code> - Underline</h4>
                        <p><strong>Renders as:</strong> <u>Underlined text</u></p>
                        <p><strong>Use for:</strong> Misspelled words, proper names (Chinese)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Please correct the <u>speling</u> error.</p>`} />
                        </div>
                        <Warning><strong> Caution:</strong> Underlines are often confused with links. Use sparingly.</Warning>
                    </div>

                    <div className="format-card">
                        <h4><code>&lt;sub&gt;</code> and <code>&lt;sup&gt;</code> - Subscript & Superscript</h4>
                        <p><strong>Renders as:</strong> H<sub>2</sub>O and x<sup>2</sup></p>
                        <p><strong>Use for:</strong> Chemical formulas, mathematical expressions, footnotes</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The formula for water is H<sub>2</sub>O.</p>
<p>E = mc<sup>2</sup></p>
<p>Referenced in footnote<sup>1</sup></p>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Code and Technical Text</h2>

                <div className="technical-elements">
                    <div className="tech-card">
                        <h4><code>&lt;code&gt;</code> - Inline Code</h4>
                        <p><strong>Renders as:</strong> <code>monospaced code</code></p>
                        <p><strong>Use for:</strong> Variable names, function names, short code snippets</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Use the <code>console.log()</code> function to print output.</p>
<p>The <code>background-color</code> CSS property sets the background.</p>`} />
                        </div>
                    </div>

                    <div className="tech-card">
                        <h4><code>&lt;pre&gt;</code> - Preformatted Text</h4>
                        <p><strong>Purpose:</strong> Preserves whitespace and line breaks</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<pre>
function greet(name) {
    console.log("Hello, " + name);
}
</pre>`} />
                        </div>
                        <p><strong>Renders exactly as written:</strong></p>
                        <pre>{`function greet(name) {
    console.log("Hello, " + name);
}`}</pre>
                    </div>

                    <div className="tech-card">
                        <h4><code>&lt;kbd&gt;</code> - Keyboard Input</h4>
                        <p><strong>Renders as:</strong> <kbd>Ctrl</kbd> + <kbd>C</kbd></p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Press <kbd>Ctrl</kbd> + <kbd>C</kbd> to copy.</p>
<p>Type <kbd>npm install</kbd> to install packages.</p>`} />
                        </div>
                    </div>

                    <div className="tech-card">
                        <h4><code>&lt;samp&gt;</code> - Sample Output</h4>
                        <p><strong>Purpose:</strong> Represents output from a program</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The command returned: <samp>Error: File not found</samp></p>`} />
                        </div>
                    </div>

                    <div className="tech-card">
                        <h4><code>&lt;var&gt;</code> - Variable</h4>
                        <p><strong>Purpose:</strong> Represents a mathematical or programming variable</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>The formula is <var>a</var><sup>2</sup> + <var>b</var><sup>2</sup> = <var>c</var><sup>2</sup></p>
<p>Set <var>x</var> = 10 and <var>y</var> = 20</p>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Quotations and Citations</h2>

                <div className="quote-elements">
                    <div className="quote-card">
                        <h4><code>&lt;blockquote&gt;</code> - Block Quotation</h4>
                        <p><strong>Purpose:</strong> Long quotations (separate paragraph)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<blockquote cite="https://example.com/article">
    <p>The only way to do great work is to love what you do.</p>
    <footer> <cite>Steve Jobs</cite></footer>
</blockquote>`} />
                        </div>
                        <blockquote style={{ borderLeft: '4px solid #ccc', paddingLeft: '1rem', fontStyle: 'italic' }}>
                            <p>The only way to do great work is to love what you do.</p>
                            <footer> <cite>Steve Jobs</cite></footer>
                        </blockquote>
                    </div>

                    <div className="quote-card">
                        <h4><code>&lt;q&gt;</code> - Inline Quotation</h4>
                        <p><strong>Purpose:</strong> Short quotes within a paragraph</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>As Einstein said, <q>Imagination is more important than knowledge.</q></p>`} />
                        </div>
                        <p>Renders as: As Einstein said, <q>Imagination is more important than knowledge.</q></p>
                    </div>

                    <div className="quote-card">
                        <h4><code>&lt;cite&gt;</code> - Citation</h4>
                        <p><strong>Purpose:</strong> Reference to a creative work</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>My favorite book is <cite>The Great Gatsby</cite> by F. Scott Fitzgerald.</p>
<p>Read more in <cite>HTML: The Definitive Guide</cite>.</p>`} />
                        </div>
                    </div>

                    <div className="quote-card">
                        <h4><code>&lt;abbr&gt;</code> - Abbreviation</h4>
                        <p><strong>Purpose:</strong> Abbreviations and acronyms with tooltips</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<p>Use <abbr title="HyperText Markup Language">HTML</abbr> to structure content.</p>
<p>The <abbr title="World Wide Web Consortium">W3C</abbr> maintains web standards.</p>`} />
                        </div>
                        <p>Renders as: Use <abbr title="HyperText Markup Language">HTML</abbr> to structure content.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>&lt;p&gt;</code> for paragraphs, <code>&lt;br&gt;</code> for line breaks</li>
                            <li>Choose semantic elements (<code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>)</li>
                            <li>Use <code>&lt;abbr&gt;</code> with titles for abbreviations</li>
                            <li>Use <code>&lt;code&gt;</code> for inline code references</li>
                            <li>Cite sources with <code>&lt;cite&gt;</code></li>
                            <li>Keep paragraphs concise (3-5 sentences)</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use multiple <code>&lt;br&gt;</code> for spacing (use CSS)</li>
                            <li>Use <code>&lt;b&gt;</code>/<code>&lt;i&gt;</code> when you mean importance/emphasis</li>
                            <li>Underline text that isn't a link</li>
                            <li>Write huge walls of text (break into paragraphs)</li>
                            <li>Use deprecated elements like <code>&lt;font&gt;</code>, <code>&lt;center&gt;</code></li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLParagraphs;




